import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import trainDisclaimerInfoIcon from '../../assets/train-disclaimer-info-circle.svg';
import trainDisclaimerIllustration from '../../assets/train-disclaimer-illustration.png';

export const TRAIN_COMING_SOON_DISCLAIMER_HEADING = 'Train Booking Important notice';

export const TRAIN_COMING_SOON_DISCLAIMER_PARAGRAPHS = [
  'Train information displayed on this platform is provided for reference purposes only and covers IRCTC-operated routes within India. Train ticket booking is currently not available through this platform.',
  'For reservations, cancellations, and official train services, please visit the IRCTC platform. While we strive to provide accurate and timely information, names, schedules, availability, fares, and other details may change without notice and may not always be complete, accurate, or up to date. Please verify all travel information through official IRCTC sources before making travel plans.',
];

export const TRAIN_COMING_SOON_DISCLAIMER_TEXT =
  TRAIN_COMING_SOON_DISCLAIMER_PARAGRAPHS.join('\n\n');

/**
 * Alert dialog shown when the user enters train search surfaces.
 * Must be dismissed via Understood — backdrop and Escape do not close it.
 */
export default function TrainComingSoonDisclaimer({ open, onClose, okLabel = 'Understood' }) {
  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      disableScrollLock
      fullWidth
      aria-labelledby="train-coming-soon-disclaimer-title"
      aria-describedby="train-coming-soon-disclaimer-body"
      PaperProps={{
        sx: {
          bgcolor: '#FBFBFB',
          borderRadius: '24px',
          maxWidth: { xs: 'calc(100% - 32px)', sm: 549 },
          width: '100%',
          m: { xs: 2, sm: 'auto' },
          fontFamily: '"Host Grotesk", sans-serif',
          boxShadow: '2px 4px 6px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          fontFamily: '"Host Grotesk", sans-serif',
          p: { xs: '24px 16px', sm: '32px 24px' },
          '&.MuiDialogContent-root': {
            overflowY: 'auto',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: '20px', sm: '24px' },
            width: '100%',
            maxWidth: 501,
            mx: 'auto',
          }}
        >
          {/* Header — info icon + title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: '12px', sm: '16px' },
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={trainDisclaimerInfoIcon}
              alt=""
              aria-hidden
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                display: 'block',
              }}
            />
            <Typography
              id="train-coming-soon-disclaimer-title"
              component="h2"
              sx={{
                fontFamily: '"Host Grotesk", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '22px', sm: '28px' },
                lineHeight: { xs: '28px', sm: '32px' },
                color: '#1A1827',
                letterSpacing: 0,
                textAlign: { xs: 'left', sm: 'center' },
              }}
            >
              {TRAIN_COMING_SOON_DISCLAIMER_HEADING}
            </Typography>
          </Box>

          {/* Train illustration with ambient blur */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 279,
              height: { xs: 'auto', sm: 205 },
              aspectRatio: '279 / 205',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '93%',
                height: '93%',
                backgroundImage: `url(${trainDisclaimerIllustration})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(37px)',
                opacity: 0.45,
                pointerEvents: 'none',
              }}
            />
            <Box
              component="img"
              src={trainDisclaimerIllustration}
              alt=""
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          {/* Body copy */}
          <Box
            id="train-coming-soon-disclaimer-body"
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            {TRAIN_COMING_SOON_DISCLAIMER_PARAGRAPHS.map((paragraph, index) => (
              <Typography
                key={paragraph.slice(0, 32)}
                sx={{
                  fontFamily: '"Host Grotesk", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '14px', sm: '16px' },
                  lineHeight: '20px',
                  color: '#717180',
                  letterSpacing: 0,
                  mb: index === 0 ? '20px' : 0,
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>

          {/* Primary action */}
          <Button
            variant="contained"
            onClick={onClose}
            fullWidth
            sx={{
              fontFamily: '"Host Grotesk", sans-serif',
              textTransform: 'none',
              bgcolor: '#1A1827',
              color: '#FBFBFB',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '22px',
              minHeight: { xs: 52, sm: 58 },
              height: { xs: 52, sm: 58 },
              borderRadius: '16px',
              px: '16px',
              py: '8px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#333333',
                boxShadow: 'none',
              },
            }}
          >
            {okLabel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
